(ns fen.login
  "Asking to be let in. One screen, not a wizard: a button that asks for a
  code, and a field for the four digits that come back.

  Nothing is sent until it is asked for. Drawing this screen is not the same
  as wanting a mail - a window that lost its key reopens it on every poll,
  and a reload should not cost anybody a message.

  It is the only thing in the window that is not a screen from the server -
  it has to work while the server is refusing to hand one out."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [clojure.string :as str]
            [fen.api :as api]
            [fen.mui :as mui :refer [sx]]
            [fen.window :as window]
            [kc.core :as kc]))

(defn- only-digits [s]
  (let [d (str/replace (or s "") #"[^0-9]" "")]
    (subs d 0 (min 4 (count d)))))

(def-ui Login []
  (let [[sent? set-sent]  (kc/use-state false)
        [code set-code]   (kc/use-state "")
        [busy? set-busy]  (kc/use-state false)
        [error set-error] (kc/use-state nil)
        send!   (fn []
                  (set-busy true)
                  (set-error nil)
                  (-> (api/request-code!)
                      (.then (fn [_]
                               (set-sent true)
                               (set-busy false)
                               ;; Asking is done with a button and answering
                               ;; with the keyboard - whoever pressed it
                               ;; should not have to reach back for the field.
                               (some-> (.getElementById js/document "ento-code")
                                       .focus)))
                      (.catch (fn [_]
                                (set-busy false)
                                (set-error "The code could not be requested.")))))
        submit! (fn []
                  (set-busy true)
                  (set-error nil)
                  ;; A good code ends this component: the flag goes down in
                  ;; fen.api and the window draws the screen it was refused.
                  (-> (api/verify-code! code)
                      (.then (fn [_] (set-busy false) (window/draw!)))
                      (.catch (fn [_]
                                (set-busy false)
                                (set-code "")
                                (set-error "Wrong or expired code.")))))]
    ($ mui/Box {:sx (sx {:minHeight "100vh" :display "flex"
                         :alignItems "center" :justifyContent "center"})}
       ($ mui/Box {:sx (sx {:display "flex" :flexDirection "column" :gap ".75rem"
                            :padding "1.5rem" :minWidth "18rem"
                            :border "1px solid" :borderColor "divider"
                            :backgroundColor "background.paper"
                            :boxShadow "0 24px 80px rgba(0,0,0,.6)"})}
          ($ :span {:class "ento-title"}
             ($ :span {:class "ento-prompt"} "> ")
             "ento"
             ($ :span {:class "ento-caret"}))
          ($ mui/Typography {:sx (sx {:color "text.secondary"})}
             (if sent?
               "A code is on its way."
               "A code goes to your address when you ask for one."))
          ;; Four digits and nothing else. What the field lets in is said to
          ;; the phone as well as to the browser - a numeric keypad is what
          ;; comes up for this, and a full keyboard is four wrong keys.
          ($ mui/TextField
             {:id "ento-code"
              :value code
              :placeholder "____"
              :autoFocus true
              :size "small"
              :autoComplete "one-time-code"
              :slotProps (sx {:htmlInput {:inputMode "numeric"
                                          :pattern "[0-9]*"
                                          :maxLength 4
                                          :style {:letterSpacing ".5em"}}})
              :onChange (fn [^js e] (set-code (only-digits (-> e .-target .-value))))
              :onKeyDown (fn [^js e]
                           (when (and (= "Enter" (.-key e)) (= 4 (count code)))
                             (.preventDefault e)
                             (submit!)))})
          (when error
            ($ mui/Alert {:severity "error" :variant "outlined"} error))
          ($ mui/Box {:sx (sx {:display "flex" :alignItems "center" :gap ".5rem"})}
             ;; The same button either way, and it says which one it is: the
             ;; first press is what starts the whole thing, every one after
             ;; it is somebody who did not get the mail.
             ($ mui/Button {:disabled busy?
                            :variant "outlined"
                            :color (if sent? "inherit" "primary")
                            :onClick (fn [_] (send!))
                            :sx (when sent? (sx {:color "text.secondary" :borderColor "divider"}))}
                (if sent? "new code" "request code"))
             ($ :span {:style {:flex "1"}})
             ($ mui/Button {:disabled (or busy? (not= 4 (count code)))
                            :variant "outlined"
                            :color "primary"
                            :onClick (fn [_] (submit!))}
                "sign in"))))))
